import { getTrendingValue } from '@pendo/middleware';

export const VISITOR_STICKINESS = 'visitorStickiness';

export const ACCOUNT_STICKINESS = 'accountStickiness';

export const STICKINESS_METRIC_KEYS = [VISITOR_STICKINESS, ACCOUNT_STICKINESS] as const;

export type StickinessMetricKey = (typeof STICKINESS_METRIC_KEYS)[number];

export const STICKINESS_KIND_BY_KEY: Record<StickinessMetricKey, 'visitor' | 'account'> = {
    [VISITOR_STICKINESS]: 'visitor',
    [ACCOUNT_STICKINESS]: 'account',
};

export interface StickinessSubtype {
    id: string;
    lookBackCountNumerator: number;
    lookBackCountDenominator: number;
    acronym: Record<'visitor' | 'account', string>;
}

export const STICKINESS_SUBTYPES: StickinessSubtype[] = [
    { id: 'dauMau', lookBackCountNumerator: 1, lookBackCountDenominator: 30, acronym: { visitor: 'DAU/MAU', account: 'DAA/MAA' } },
    { id: 'dauWau', lookBackCountNumerator: 1, lookBackCountDenominator: 7, acronym: { visitor: 'DAU/WAU', account: 'DAA/WAA' } },
    { id: 'wauMau', lookBackCountNumerator: 7, lookBackCountDenominator: 30, acronym: { visitor: 'WAU/MAU', account: 'WAA/MAA' } },
];

export const DEFAULT_STICKINESS_SUBTYPE = 'dauMau';

// Include weekends by default; users can exclude per column in Manage columns.
export const DEFAULT_STICKINESS_WEEKENDS_HIDDEN = false;

export function isStickinessMetricKey (metricKey: string): metricKey is StickinessMetricKey {
    return (STICKINESS_METRIC_KEYS as readonly string[]).includes(metricKey);
}

export function getStickinessSubtype (subtypeId: string | undefined): StickinessSubtype {
    return (
        STICKINESS_SUBTYPES.find((subtype) => subtype.id === subtypeId) ??
        STICKINESS_SUBTYPES.find((subtype) => subtype.id === DEFAULT_STICKINESS_SUBTYPE)!
    );
}

export interface StickinessColumnConfig {
    subtype?: string;
    weekendsHidden?: boolean;
}

export interface ResolvedStickinessParams {
    kind: 'visitor' | 'account';
    lookBackCountNumerator: number;
    lookBackCountDenominator: number;
    weekendsHidden: boolean;
}

export function resolveStickinessAggParams (
    metricKey: string,
    config: StickinessColumnConfig = {},
): ResolvedStickinessParams {
    const kind = STICKINESS_KIND_BY_KEY[metricKey as StickinessMetricKey] ?? 'visitor';
    const subtype = getStickinessSubtype(config.subtype);

    return {
        kind,
        lookBackCountNumerator: subtype.lookBackCountNumerator,
        lookBackCountDenominator: subtype.lookBackCountDenominator,
        weekendsHidden: config.weekendsHidden ?? DEFAULT_STICKINESS_WEEKENDS_HIDDEN,
    };
}

const STICKINESS_COLUMN_SEPARATOR = '__';

export function buildStickinessColumnKey (metricKey: string, subtypeId: string): string {
    return `${metricKey}${STICKINESS_COLUMN_SEPARATOR}${subtypeId}`;
}

export function parseStickinessColumnKey (
    columnKey: string | undefined,
): { metricKey: StickinessMetricKey; subtypeId: string } | null {
    if (!columnKey) return null;

    const [metricKey, subtypeId] = columnKey.split(STICKINESS_COLUMN_SEPARATOR);
    if (!isStickinessMetricKey(metricKey) || !subtypeId) return null;

    return { metricKey, subtypeId };
}

export function isStickinessColumnKey (columnKey: string | undefined): boolean {
    return parseStickinessColumnKey(columnKey) !== null;
}

export function stickinessColumnKeysFor (metricKey: StickinessMetricKey): string[] {
    return STICKINESS_SUBTYPES.map((subtype) => buildStickinessColumnKey(metricKey, subtype.id));
}

export function getStickinessColumnKey (
    column: { columnKey?: string; property?: string; prop?: string } | undefined,
): string | undefined {
    return column?.columnKey ?? column?.property ?? column?.prop;
}

export function getStickinessColumnBase (
    column: { columnKey?: string; property?: string; prop?: string } | undefined,
): string | undefined {
    const columnKey = getStickinessColumnKey(column);
    const parsed = parseStickinessColumnKey(columnKey);

    return parsed ? parsed.metricKey : columnKey;
}

type TranslateFn = (key: string) => string;

type StickinessColumnLike = { columnKey?: string; property?: string; prop?: string };

export function stickinessColumnLabel ($t: TranslateFn, metricKey: string): string {
    return metricKey === ACCOUNT_STICKINESS
        ? $t('metricGrid.Account stickiness')
        : $t('metricGrid.Visitor stickiness');
}

export function stickinessFormulaColumnLabel ($t: TranslateFn, columnKey: string): string {
    const parsed = parseStickinessColumnKey(columnKey);
    if (!parsed) return columnKey;

    const kind = STICKINESS_KIND_BY_KEY[parsed.metricKey];
    const subtype = getStickinessSubtype(parsed.subtypeId);

    return `${stickinessColumnLabel($t, parsed.metricKey)} (${subtype.acronym[kind]})`;
}

export function dedupeStickinessOptions (
    $t: TranslateFn,
    options: StickinessColumnLike[],
    selectedColumns: StickinessColumnLike[],
    index: number,
): StickinessColumnLike[] {
    const currentKey = getStickinessColumnKey((selectedColumns || [])[index]);
    const selectedKeys = new Set(
        (selectedColumns || [])
            .map((column) => getStickinessColumnKey(column))
            .filter((key) => key != null && key !== currentKey),
    );
    const familyOf = (option: StickinessColumnLike) => parseStickinessColumnKey(getStickinessColumnKey(option))?.metricKey;
    const seenFamilies = new Set<string>();

    return (options || []).reduce<StickinessColumnLike[]>((deduped, option) => {
        const family = familyOf(option);
        if (!family) {
            if (!selectedKeys.has(getStickinessColumnKey(option))) deduped.push(option);

            return deduped;
        }
        if (seenFamilies.has(family)) return deduped;

        seenFamilies.add(family);
        const firstUnused = (options || [])
            .filter((opt) => familyOf(opt) === family)
            .find((opt) => !selectedKeys.has(getStickinessColumnKey(opt)));
        if (firstUnused) {
            deduped.push({ ...firstUnused, label: stickinessColumnLabel($t, family) } as StickinessColumnLike);
        }

        return deduped;
    }, []);
}

export function resolveStickinessWeekendsHidden (
    column: { weekendsHidden?: boolean } | null | undefined,
    columnKey: string | undefined,
    config: Record<string, { weekendsHidden?: boolean }> = {},
): boolean {
    return column?.weekendsHidden
        ?? (columnKey ? config[columnKey]?.weekendsHidden : undefined)
        ?? DEFAULT_STICKINESS_WEEKENDS_HIDDEN;
}

export type StickinessWeekendsConfig = Record<string, { weekendsHidden?: boolean }>;

type StickinessColumnConfigInput = {
    weekendsHidden?: boolean;
    columnKey?: string;
    property?: string;
    prop?: string;
};

export function buildStickinessConfigFromColumns (
    columns: ReadonlyArray<StickinessColumnConfigInput>,
    existingConfig: StickinessWeekendsConfig = {},
): StickinessWeekendsConfig {
    const nextConfig: StickinessWeekendsConfig = {};
    columns.forEach((column) => {
        const columnKey = getStickinessColumnKey(column);
        if (!columnKey || !parseStickinessColumnKey(columnKey)) return;

        nextConfig[columnKey] = {
            weekendsHidden: resolveStickinessWeekendsHidden(column, columnKey, existingConfig),
        };
    });

    return nextConfig;
}

export function parseStickinessRatio (
    row: { failed?: boolean; [key: string]: unknown } | undefined,
    field = 'ratioPercentageSummary',
): number | '--' | undefined {
    if (row?.failed) return '--';
    const value = row?.[field];
    if (typeof value !== 'number') return undefined;

    return Math.round(value * 10) / 10;
}

export function getStickinessRowTrend (
    currentRow: { [key: string]: unknown } | undefined,
    previousRow: { [key: string]: unknown } | undefined,
    field = 'ratioPercentageSummary',
): string | undefined {
    const current = currentRow?.[field];
    const previous = previousRow?.[field];
    if (typeof current !== 'number' || typeof previous !== 'number') return undefined;

    return getTrendingValue(previous, current);
}

type StickinessSummaryRow = {
    failed?: boolean;
    ratioPercentageSummary?: number;
    prevRatioPercentageSummary?: number;
};

function resolveStickinessSummaryRow (value: unknown): StickinessSummaryRow | undefined {
    if (Array.isArray(value)) {
        return value.find((item) => typeof item?.ratioPercentageSummary === 'number') as StickinessSummaryRow | undefined;
    }

    return value as StickinessSummaryRow | undefined;
}

// Normalizes a stickiness summary agg row (or array wrapper) into a display
// value plus period-over-period trend. Uses full-precision ratios for trending.
export function parseStickinessSummaryRow (
    value: unknown,
): { current?: number; trend?: string } {
    const row = resolveStickinessSummaryRow(value);
    const current = parseStickinessRatio(row);
    if (current === '--' || current === undefined) {
        return { current: undefined, trend: undefined };
    }

    const trend = getStickinessRowTrend(
        row,
        typeof row?.prevRatioPercentageSummary === 'number'
            ? { ratioPercentageSummary: row.prevRatioPercentageSummary }
            : undefined,
    );

    return { current, trend };
}
